import React, { createContext, useContext, useRef, useCallback } from "react";
import axios from "axios";

const API = "/api";

const FolderFileContext = createContext();

export function FolderFileProvider({ children }) {
  // Use ref to avoid rerendering when context cache updates
  const foldersRef = useRef({});
  const filesRef = useRef({});

  // Add/update folders
  const addFolders = useCallback((folders) => {
    if (!Array.isArray(folders)) return;
    folders.forEach((f) => {
      foldersRef.current[f.id] = f;
    });
  }, []);
  const updateFolder = useCallback((folder) => {
    if (folder && folder.id) {
      foldersRef.current[folder.id] = folder;
    }
  }, []);
  const removeFolder = useCallback((id) => {
    if (id) delete foldersRef.current[id];
  }, []);

  // Add/update files
  const addFiles = useCallback((files) => {
    if (!Array.isArray(files)) return;
    files.forEach((f) => {
      filesRef.current[f.id] = f;
    });
  }, []);
  const updateFile = useCallback((file) => {
    if (file && file.id) {
      filesRef.current[file.id] = file;
    }
  }, []);
  const removeFile = useCallback((id) => {
    if (id) delete filesRef.current[id];
  }, []);

  // Fetch single folder (with API fallback)
  const fetchFolder = useCallback(async (id, token) => {
    if (!id) return null;
    if (foldersRef.current[id]) return foldersRef.current[id];
    try {
      const res = await axios.get(`${API}/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const folder = res.data;
      foldersRef.current[folder.id] = folder;
      return folder;
    } catch {
      return null;
    }
  }, []);

  // Fetch single file (with API fallback)
  const fetchFile = useCallback(async (id, token) => {
    if (!id) return null;
    if (filesRef.current[id]) return filesRef.current[id];
    try {
      const res = await axios.get(`${API}/files/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const file = res.data;
      filesRef.current[file.id] = file;
      return file;
    } catch {
      return null;
    }
  }, []);

  return (
    <FolderFileContext.Provider
      value={{
        folders: foldersRef.current,
        files: filesRef.current,
        addFolders,
        addFiles,
        updateFolder,
        updateFile,
        removeFolder,
        removeFile,
        fetchFolder,
        fetchFile,
      }}
    >
      {children}
    </FolderFileContext.Provider>
  );
}

export function useFolderFile() {
  return useContext(FolderFileContext);
}
